/**
 * Team → Review queue — prototype `review` (docs/prototype.html 1684): what waits for a decision.
 * GET /review-queue (every page). Two panels: task change requests (Decide when the caller may decide) and
 * closures with proof to check (Looks right / Send back when the caller may check — never their own, and the
 * associate director's closures go to the founder). Task proofs go to POST /closures/{task_id}/check; proofs on
 * client requests and escalations to POST /closures/{item_kind}/{item_id}/check.
 * Email proposals not answered in PROPOSED_LATE_HOURS and incidents not reviewed yet each get their own panel; the
 * button follows the route the server gives (the thread with Confirm / Reject; the entry on the Appraisal record).
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { CAPTURE_HELD_LATE_HOURS, CHANGE_REASON_LABEL, type ReviewItem } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../../api';
import { firstName, plural } from '../../lib/format';
import { useReviewQueue, useWorkWrite } from '../../lib/work';
import { ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import { DecideChangeModal, SendBackModal } from '../work/TaskModals';
import { panelSearch } from '../requests/ItemPanels';
import { useAutoLog, useHeld, useHeldBacklog, useCaptureSettings } from '../../lib/capture';
import { AutoLogTable, HeldList } from '../capture/HeldList';
import { useSession } from '../../lib/session';

type ReviewChange = Extract<ReviewItem, { kind: 'change' }>;
type Proof = Extract<ReviewItem, { kind: 'proof' | 'request_proof' | 'escalation_proof' }>;
type Routed = Extract<ReviewItem, { kind: 'proposed' | 'incident' }>;
const KIND_CHIP: Record<Proof['kind'], string> = { proof: 'Task', request_proof: 'Request', escalation_proof: 'Escalation' };

/** "whatsapp_script_v2.docx" from a link, or the host when the path says nothing. */
function linkLabel(url: string): string {
  try {
    const u = new URL(url);
    const last = decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() ?? '');
    return last && last.length <= 60 ? last : u.host;
  } catch {
    return url.slice(0, 60);
  }
}

/** Drawn above the held tabs (prototype review render); nothing at all while nothing waits. */
function ReviewDecisions() {
  const q = useReviewQueue();
  const nav = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  const [deciding, setDeciding] = useState<ReviewChange | null>(null);
  const [sendBack, setSendBack] = useState<Proof | null>(null);

  const check = useWorkWrite((v: { p: Proof; outcome: 'ok' | 'send_back'; note?: string; key: string }) => {
    const body = { outcome: v.outcome, ...(v.note ? { note: v.note } : {}) };
    const sent: Promise<unknown> = v.p.kind === 'proof'
      ? call('closures.check', { params: { task_id: v.p.task_id }, idempotencyKey: v.key, body })
      : call('closures.checkItem', {
          params: { item_kind: v.p.kind === 'request_proof' ? 'client_request' : 'escalation', item_id: v.p.item_id },
          idempotencyKey: v.key,
          body,
        });
    return sent;
  });
  const doCheck = (p: Proof, outcome: 'ok' | 'send_back', note?: string) =>
    check.mutate(
      { p, outcome, note, key: newIdempotencyKey() },
      {
        onSuccess: () => {
          setSendBack(null);
          toast(outcome === 'ok' ? 'Checked. It stays closed.' : `Sent back to ${firstName(p.closure.closed_by.name)}`);
        },
        onError: (e) => toast(errorText(e)),
      },
    );

  if (q.isLoading)
    return (
      <div className="panel">
        <Loading />
      </div>
    );
  if (q.error)
    return (
      <div className="panel">
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );

  const items: ReviewItem[] = (q.data ?? []).filter((i) => i.kind !== 'held');
  const changes = items.filter((i): i is ReviewChange => i.kind === 'change');
  const proofs = items.filter((i): i is Proof => i.kind === 'proof' || i.kind === 'request_proof' || i.kind === 'escalation_proof');
  // Held voice items have their own tab (step 9); email proposals and incidents still show here.
  const proposals = items.filter((i): i is Routed => i.kind === 'proposed');
  const incidents = items.filter((i): i is Routed => i.kind === 'incident');

  if (!items.length) return null;

  return (
    <>
      {changes.length ? (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="hd">
            <h3>Task change requests</h3>
            <div className="r">{changes.some((c) => c.can_decide) ? 'You decide' : 'Waiting for the Ops associate director'}</div>
          </div>
          {changes.map((c) => {
            const cr = c.change_request;
            return (
              <div className="li" key={c.id}>
                <div className="b">
                  <div className="t">{cr.task_title}</div>
                  <div className="m">
                    {cr.client ? <span>{cr.client.name}</span> : <span>Internal</span>}
                    <span>
                      {firstName(cr.by.name)} asks: {CHANGE_REASON_LABEL[cr.reason_code]}
                    </span>
                    {cr.suggested_user ? <span>Suggests {firstName(cr.suggested_user.name)}</span> : null}
                    {cr.note ? <span>{cr.note}</span> : null}
                  </div>
                </div>
                {c.can_decide ? (
                  <button className="btn sm pri" onClick={() => setDeciding(c)}>
                    Decide
                  </button>
                ) : (
                  <span className="chip">Waiting</span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {proofs.length ? (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="hd">
            <h3>Closed, proof to check</h3>
            <div className="r">{proofs.length} waiting</div>
          </div>
          {proofs.map((p) => {
            const c = p.closure;
            return (
              <div className="li" key={p.id}>
                <div className="b">
                  <div className="t">
                    <a
                      href={proofLink(p, loc.pathname, loc.search)}
                      onClick={(e) => {
                        e.preventDefault();
                        nav(proofLink(p, loc.pathname, loc.search));
                      }}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {p.title}
                    </a>
                  </div>
                  <div className="m">
                    <span className="chip">{KIND_CHIP[p.kind]}</span>
                    {p.client ? <span>{p.client.name}</span> : <span>Internal</span>}
                    {c.proof_url ? (
                      <span>
                        <a href={c.proof_url} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}>
                          {linkLabel(c.proof_url)}
                        </a>
                      </span>
                    ) : c.no_attachment_needed ? (
                      <span>No attachment needed</span>
                    ) : null}
                    {c.proof_text ? <span>{c.proof_text}</span> : null}
                    {c.next_step ? <span>Next: {c.next_step}</span> : null}
                    <span>
                      by {firstName(c.closed_by.name)}
                      {c.closed_by_on_behalf_of ? `, acting as ${firstName(c.closed_by_on_behalf_of.name)}` : ''}
                    </span>
                  </div>
                </div>
                {p.can_check ? (
                  <>
                    <button className="btn sm bad" disabled={check.isPending} onClick={() => setSendBack(p)}>
                      Send back
                    </button>
                    <button className="btn sm good" disabled={check.isPending} onClick={() => doCheck(p, 'ok')}>
                      Looks right
                    </button>
                  </>
                ) : (
                  <span className="chip">Someone else checks this</span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Late email proposals open their thread with Confirm / Reject; unreviewed incidents open the entry on the
          Appraisal record with "Review and decide the cause". Two kinds of decision, so two panels (the held voice
          items have their own "Held for review" tab below). */}
      <RoutedPanel title="Email proposals not answered" action="Confirm or reject" rows={proposals} onOpen={(o) => nav(o.route)} />
      <RoutedPanel title="Incidents to review" action="Decide the cause" rows={incidents} onOpen={(o) => nav(o.route)} />

      {deciding ? <DecideChangeModal cr={deciding.change_request} onClose={() => setDeciding(null)} /> : null}
      {sendBack ? (
        <SendBackModal title={sendBack.title} by={firstName(sendBack.closure.closed_by.name)} busy={check.isPending} onClose={() => setSendBack(null)} onSend={(note) => doCheck(sendBack, 'send_back', note)} />
      ) : null}
    </>
  );
}

/** A panel of review rows that are decided on another screen: each row's Open goes to the route the server gives. */
function RoutedPanel({ title, action, rows, onOpen }: { title: string; action: string; rows: Routed[]; onOpen: (o: Routed) => void }) {
  if (!rows.length) return null;
  return (
    <div className="panel">
      <div className="hd">
        <h3>{title}</h3>
        <div className="r">{rows.length} waiting</div>
      </div>
      {rows.map((o) => (
        <div className="li" key={o.id}>
          <div className="b">
            <div className="t">{o.title}</div>
            <div className="m">
              {o.by ? <span>From {firstName(o.by.name)}</span> : null}
              {o.client ? <span>{o.client.name}</span> : null}
              {o.detail ? <span>{o.detail}</span> : null}
            </div>
          </div>
          <button className="btn sm" onClick={() => onOpen(o)}>
            {action}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Where a proof's title goes: the item's own side panel on this page (the panel host opens on ?task=,
 * ?request= and ?escalation=), keeping the page's other query params. A task proof opens the task, a client
 * request proof the request, an escalation proof the escalation.
 */
function proofLink(p: Proof, path: string, search: string): string {
  if (p.kind === 'proof') return `${path}${panelSearch(search, 'task', p.task_id)}`;
  return `${path}${panelSearch(search, p.kind === 'request_proof' ? 'request' : 'escalation', p.item_id)}`;
}

/**
 * Team → Review queue with the prototype's step-9 tabs (VIEWS.review, docs/prototype.html L1222): what waits for a
 * decision (change requests, closures to check), "Held for review" (GET /held-items, oldest first, late after 4 h,
 * with the backlog line from GET /held-items/backlog) and the "Auto-approved log" (GET /capture/auto-log).
 */
export function ReviewQueue() {
  const loc = useLocation();
  const nav = useNavigate();
  const { can } = useSession();
  const sp = new URLSearchParams(loc.search);
  // GET /review-queue gives held rows the route /team/review?held=<id>: open the Held tab on that row.
  const focusHeld = sp.get('held');
  // Prototype review render: the decision cards (change requests, closures to check) stand above the note, and the
  // tabs are Held for review (default) and Auto-approved log. An old ?rq=decide link lands on Held.
  const tab = sp.get('rq') === 'auto' ? 'auto' : 'held';
  const held = useHeld();
  const auto = useAutoLog(true, 100);
  const backlog = useHeldBacklog(can('tasks.read.all'));
  const settings = useCaptureSettings();
  const setTab = (t: string) => {
    const q = new URLSearchParams(loc.search);
    q.delete('held');
    if (t === 'held') q.delete('rq');
    else q.set('rq', t);
    const s = q.toString();
    nav(`${loc.pathname}${s ? `?${s}` : ''}`, { replace: true });
  };
  const late = (held.data ?? []).filter((h) => h.is_late).length;
  const thr = settings.data?.auto_threshold ?? 85;
  const b = backlog.data;
  return (
    <>
      <ReviewDecisions />
      <div className="note" style={{ marginBottom: 16 }}>
        Tasks at {thr}% confidence or more are approved automatically after the {settings.data ? Math.round(settings.data.countdown_seconds / 60) : 2}-minute timer. Everything below that waits here for a person.
      </div>
      <div className="tabs" role="tablist">
        {(
          [
            ['held', 'Held for review', held.data?.length ?? null],
            ['auto', 'Auto-approved log', auto.data?.data.length ?? null],
          ] as const
        ).map(([k, l, n]) => (
          <button key={k} className={tab === k ? 'on' : ''} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {l}
            {n !== null ? (
              <span className="n" style={k === 'held' && late ? { color: 'var(--cold)', fontWeight: 600 } : undefined}>
                {n}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      {tab === 'held' ? (
        <>
          {b ? (
            // The prototype hides stat tiles inside a hub, so the backlog (ADR-002 §17.3: live from day one) is one line.
            <div className={`note${b.late ? ' warn' : ''}`} style={{ marginBottom: 12 }}>
              <b>{plural(b.open, 'item')} open</b>
              {b.late ? `, ${b.late} waiting over ${CAPTURE_HELD_LATE_HOURS} hours` : ''}
              {b.oldest_age_hours !== null ? `. Oldest ${Math.floor(b.oldest_age_hours)}h` : ''}. {b.created_today} held and {b.resolved_today} resolved today.
              {b.held_rate_7d_pct !== null ? ` Over 7 days, ${Math.round(b.held_rate_7d_pct)}% of tasks from notes were held.` : ''}
            </div>
          ) : null}
          <div className="panel">
            <HeldList q={held} focus={focusHeld} />
          </div>
        </>
      ) : (
        <div className="panel">
          <AutoLogTable q={auto} />
        </div>
      )}
    </>
  );
}
